import { useTrackAnalyticsEvent } from "core/analytics/analytics.hooks";
import { APP_NAME } from "core/types/app.constants";
import { FC, PropsWithChildren } from "react";

import { BlocksQueryProvider } from "@bps/blocks-core";
import { PremierCoreStoreProvider } from "@bps/blocks-premier";
import { AuthorizationMiddleware } from "@bps/http-client";
import { getAccessToken } from "@bps/msal";
import { useQueryClient } from "@tanstack/react-query";

import { usePermissionsChecker } from "../auth/usePermissionsChecker";
import { useFeedbackStore } from "../stores";
import { Permission } from "../types/permission.enum";

const config = window.appConfig;

const blocksConfig = {
  fhir: {
    baseURL: `${config.fhirBaseUrl}`,
    overrideMiddlewares: {
      request: [new AuthorizationMiddleware(getAccessToken)],
      respond: []
    }
  },
  bpx: {
    baseURL: `${config.bpxBaseUrl}`,
    overrideMiddlewares: {
      request: [new AuthorizationMiddleware(getAccessToken)],
      respond: []
    }
  },
  pltCatalog: {
    baseURL: `${config.pltCatalogBaseUrl}`,
    overrideMiddlewares: {
      request: [new AuthorizationMiddleware(getAccessToken)],
      respond: []
    }
  },
  bpCoreDocument: {
    baseURL: `${config.bpCoreDocumentBaseUrl}`,
    overrideMiddlewares: {
      request: [new AuthorizationMiddleware(getAccessToken)],
      respond: []
    }
  }
};

type BlocksProviderProps = PropsWithChildren;

export const BlocksProvider: FC<BlocksProviderProps> = ({ children }) => {
  const feedback = useFeedbackStore.getState();
  const queryClient = useQueryClient();
  const hasPermission = usePermissionsChecker();
  const trackAnalytics = useTrackAnalyticsEvent();

  const hasProsPermission = hasPermission(
    [
      Permission.ProsBpmWebAllowed,
      Permission.PerPltUserRead,
      Permission.PerPltUserWrite,
      Permission.PerPltLicenceRead,
      Permission.PerPltLicenceWrite,
      Permission.PerPltNetworkRead,
      Permission.PerPltNetworkWrite
    ],
    "or"
  );

  return (
    <BlocksQueryProvider
      queryClient={queryClient}
      blocksEndpoints={blocksConfig}
      config={{
        isDevelopment: import.meta.env.MODE === "development",
        disabledCoreFetcher: !hasProsPermission,
        consumerAppName: APP_NAME
      }}
      logAnalyticsEvent={trackAnalytics}
      feedback={{
        error: feedback.error,
        info: feedback.info,
        warning: feedback.warning,
        success: feedback.success
      }}
    >
      <PremierCoreStoreProvider>{children}</PremierCoreStoreProvider>
    </BlocksQueryProvider>
  );
};
